'use client'

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import {
  loginUser,
  getUser,
  updateUserProfile,
  followUser,
  unfollowUser,
  followToken,
  unfollowToken,
} from '../api/user'
import { authLogin, getNonce } from '../api/auth'
import type { User } from '../api/user'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import { useConnect, useAccount, useDisconnect, useSignMessage } from 'wagmi'
import { useLocalStorage } from '@uidotdev/usehooks'

export type UserProviderContextType = {
  isLoggedIn: boolean
  user: User | null
  followedUserIds: number[]
  followedTokenIds: number[]
  login: () => Promise<User | undefined>
  fetchUser: () => Promise<User | undefined>
  updateProfile: (data: any, file: any) => Promise<User | undefined>
  follow: (followedId: number) => Promise<any>
  unfollow: (followedId: number) => Promise<any>
  followToken: (tokenId: number) => Promise<any>
  unfollowToken: (tokenId: number) => Promise<any>
}

const defaultState: UserProviderContextType = {
  isLoggedIn: false,
  user: null,
  followedUserIds: [],
  followedTokenIds: [],
  login: async () => undefined,
  fetchUser: async () => undefined,
  updateProfile: async () => undefined,
  follow: async () => undefined,
  unfollow: async () => undefined,
  followToken: async () => undefined,
  unfollowToken: async () => undefined,
}

const UserProviderContext = createContext<UserProviderContextType>(defaultState)

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setToken] = useLocalStorage<string | null>('volatile-auth', null)
  const [user, setUser] = useState<User | null>(null)

  const [followedUserIds, setFollowedUserIds] = useState<number[]>([])
  const [followedTokenIds, setFollowedTokenIds] = useState<number[]>([])

  const { connect, connectors, } = useConnect()
  const { address, isConnected } = useAccount()
  const { disconnect } = useDisconnect()
  const { signMessageAsync } = useSignMessage()
  const { openConnectModal } = useConnectModal()

  // Debug wagmi state
  useEffect(() => {
    console.log('Wagmi state:', { address, isConnected })
  }, [address, isConnected])

  useEffect(() => {
    const tryAuth = async () => {
      // Only auto-login if we're connected, have an address, but no token yet
      if (isConnected && address && !token && !user) {
        try {
          const nonce = await getNonce(address)
          const signature = await signMessageAsync({ message: nonce })
          const loginRes = await authLogin(address, signature)
          setToken(loginRes)
        } catch (error) {
          console.error('Auto-login failed:', error)
        }
      }
    }
    tryAuth()
  }, [isConnected, address]) // Remove token and signMessageAsync from deps to prevent loops

  useEffect(() => {
    const tryFetchUser = async () => {
      if (token && !user) {
        try {
          const userData = await getUser(token)
          setUser(userData)

          console.log(userData)

          setFollowedUserIds(userData?.userFollowed?.map((u: any) => Number(u.followedId)) || [])
          setFollowedTokenIds(userData?.followedTokens?.map((t: any) => Number(t.tokenId)) || [])
        } catch (error) {
          console.error('Failed to fetch user data:', error)
          // If token is invalid, clear it
          setToken(null)
          setUser(null)
        }
      }
    }
    tryFetchUser()
  }, [token]) // Only depend on token, not user

  const login = useCallback(async () => {
    if (!isConnected || !address) {
      openConnectModal!()
      return undefined
    }
    
    // If connected but no token, manually trigger signing process
    if (isConnected && address && !token) {
      try {
        const nonce = await getNonce(address)
        const signature = await signMessageAsync({ message: nonce })
        const loginRes = await authLogin(address, signature)
        setToken(loginRes)
        
        // Fetch user data immediately after successful login
        try {
          const userData = await getUser(loginRes)
          setUser(userData)
          setFollowedUserIds(userData?.userFollowed?.map((u: any) => Number(u.followedId)) || [])
          setFollowedTokenIds(userData?.followedTokens?.map((t: any) => Number(t.tokenId)) || [])
          return userData
        } catch (userError) {
          console.error('Failed to fetch user data after login:', userError)
          return undefined
        }
      } catch (error) {
        console.error('Manual login failed:', error)
        return undefined
      }
    }
    
    return user ?? undefined
  }, [isConnected, address, token, openConnectModal, signMessageAsync, user])

  const fetchUser = useCallback(async () => {
    const stored = localStorage.getItem('volatile-auth')
    const tokenFromStorage = stored ? JSON.parse(stored).token : null
    if (!tokenFromStorage) return
    
    try {
      const userData = await getUser(tokenFromStorage)
      setToken(tokenFromStorage)
      setUser(userData)

      setFollowedUserIds(userData?.followedUsers?.map((u: any) => Number(u.followedId)) || [])
      setFollowedTokenIds(userData?.followedTokens?.map((t: any) => Number(t.tokenId)) || [])

      return userData
    } catch (error) {
      console.error('Token invalid, clearing storage:', error)
      localStorage.removeItem('volatile-auth')
      setToken(null)
      setUser(null)
      return null
    }
  }, [])

  const updateProfile = useCallback(
    async (data: any, file : any) => {
      console.log(data)
      const updated = await updateUserProfile(data, file)
      
      await fetchUser() 
      return updated
    },
    [token, fetchUser]
  )

  const follow = useCallback(
    async (followedId: number) => {
      if (!token) return
      await followUser(token, followedId)
      setFollowedUserIds(prev => [...prev, Number(followedId)])
    },
    [token, setFollowedUserIds]
  )
  const unfollow = useCallback(
    async (followedId: number) => {
      if (!token) return
      await unfollowUser(token, followedId)
      setFollowedUserIds(prev => prev.filter(id => id !== Number(followedId)))
    },
    [token, setFollowedUserIds]
  )

  const followTokenFn = useCallback(
    async (tokenId: number) => {
      if (!token) return
      followToken(token, tokenId).then(() => {
      setFollowedTokenIds(prev => [...prev, Number(tokenId)])
      })
    },
    [token]
  )
  const unfollowTokenFn = useCallback(
    async (tokenId: number) => {
      if (!token) return
      unfollowToken(token, tokenId).then(() => {
        setFollowedTokenIds(prev => prev.filter(id => id !== Number(tokenId)))
      })
    },
    [token]
  )

  useEffect(() => {
    // Check for existing token on mount
    const initializeAuth = async () => {
      const stored = localStorage.getItem('volatile-auth')
      if (stored) {
        try {
          const tokenFromStorage = JSON.parse(stored).token
          if (tokenFromStorage) {
            setToken(tokenFromStorage)
          }
        } catch (error) {
          console.error('Invalid token in storage:', error)
          localStorage.removeItem('volatile-auth')
        }
      }
    }
    initializeAuth()
  }, [])

  useEffect(() => {
    fetchUser()
  }, [])

  const isLoggedIn = Boolean(token && isConnected)


  
  // Debug logging to see what's happening
  useEffect(() => {
    console.log('Auth state check:', {
      token: token,
      hasToken: !!token,
      isConnected,
      isLoggedIn,
      localStorageValue: localStorage.getItem('volatile-auth')
    })
  }, [token, isConnected, isLoggedIn])

  return (
    <UserProviderContext.Provider
      value={{
        isLoggedIn,
        user,
        followedUserIds,
        followedTokenIds,
        login,
        fetchUser,
        updateProfile,
        follow,
        unfollow,
        followToken: followTokenFn,
        unfollowToken: unfollowTokenFn,
      }}
    >
      {children}
    </UserProviderContext.Provider>
  )
}

export const useUserProvider = () => useContext(UserProviderContext)